import { message } from 'antd';
import React, { useState, useEffect } from 'react';
import { exportToXlsx } from '@/lib/utils/downloadXlsx';
import { useTranslation } from 'react-i18next';
import { InstagramPostsCommentsItemModel } from '@/types/anonymously.interface';
import DpModal from '@/components/DpModal';

export default function LoadMoreModal({
  visible,
  onCancel,
  totalComments,
  loadedComments,
  commentsData,
  progressPercent,
  progressText,
  handleLoadAll,
  resetProgress,
  code,
}: {
  visible: boolean;
  onCancel: () => void;
  onUpgrade: () => void;
  totalComments: number;
  loadedComments: number;
  loadOptions: { label: string; value: number; credits: number; isLock: boolean }[];
  commentsData: any[];
  progressPercent: number;
  progressText: string;
  handleLoadAll: (
    credits: number,
    callback: (comments: InstagramPostsCommentsItemModel[], isBackEndExport?: boolean) => void
  ) => void;
  resetProgress: () => void;
  code: string;
  comment_count: number;
}) {
  const [selectedValue, setSelectedValue] = useState<'all' | 'loaded' | 'custom'>('loaded');
  const [startLoading, setStartLoading] = useState(false);
  const [loadMoreTipShow, setLoadMoreTipShow] = useState(false);
  const { t } = useTranslation();
  const handleExport = (comments?: InstagramPostsCommentsItemModel[]) => {
    // 使用最新的评论数据
    const latestCommentsData = selectedValue === 'loaded' ? commentsData : comments;
    exportToXlsx({
      filename: 'DolphinRadar Instagram Comments',
      headers: ['Username', 'Date', 'Comment Text', 'Profile URL', 'Post URL'],
      data: latestCommentsData.map((item) => [
        item.media_user_dto.full_name,
        item.comment_time,
        item.text,
        item.media_user_dto.profile_pic_url,
        `https://www.instagram.com/p/${code}/`,
      ]),
    });
  };

  const handleExportNow = () => {
    if (selectedValue === 'loaded') {
      handleExport();
      onCancel();
      message.success('Export completed');
    } else if (selectedValue == 'all') {
      if (totalComments < 200 && loadedComments < totalComments) {
        setStartLoading(true);
      }
      handleLoadAll(Math.ceil((totalComments - loadedComments) / 15), (comments, isBackEndExport) => {
        if (isBackEndExport) {
          setStartLoading(false);
          setLoadMoreTipShow(true);
          return;
        }
        handleExport(comments);
        message.success('Export completed');
      });
    }
  };

  useEffect(() => {
    if (progressPercent === 100) {
      resetProgress();
      setStartLoading(false);
      onCancel();
    }
  }, [progressPercent]);

  if (!visible) return null;
  return (
    <>
      {loadMoreTipShow && (
        <DpModal.WithGradientButton
          open={loadMoreTipShow}
          centered
          closeIcon={false}
          title={t('Export in Progress')}
          cancelText={t('Later')}
          okText={t('View Now')}
          width="80%"
          style={{ maxWidth: '400px' }}
          onCancel={onCancel}
          okButtonProps={{
            href: '/dashboard?dashboard_type=toolkit&featureType=comment',
          }}
        >
          {t(
            'This post has over 200 comments. The export task is now logged in your dashboard. You can view it now or come back later from Dashboard → Toolkit → Post Comments.'
          )}
        </DpModal.WithGradientButton>
      )}
      {!loadMoreTipShow && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-70 transition-opacity"
            onClick={() => {
              if (progressPercent === 100 || !startLoading) {
                onCancel();
              }
            }}
          />
          {!startLoading ? (
            <div className="relative bg-white rounded-2xl shadow-xl w-[450px] max-w-[90vw] lg:p-8 p-5 flex flex-col  animate-fade-in">
              <div className="text-xl text-left font-bold mb-3">{t('Export Comments')}</div>
              <div className="text-black text-base mb-7 text-left">
                {t('Choose how many comments you want to export. Credit cost is shown for each option.')}
              </div>
              <ul className="flex flex-col gap-4 mb-20" style={{ colorScheme: 'light' }}>
                <li
                  className={`flex items-center justify-between border rounded-xl px-4 py-3 transition-all cursor-pointer`}
                  onClick={() => setSelectedValue('all')}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={selectedValue === 'all'}
                      readOnly
                      className="accent-purple-500 w-4 h-4"
                    />
                    <span className="font-medium">
                      {t('All')} ({totalComments})
                    </span>
                  </div>
                  <span className="text-purple-500 font-medium text-sm whitespace-nowrap">
                    {Math.ceil((totalComments - loadedComments) / 15)} {t('Credits')}
                  </span>
                </li>
                <li
                  className={`flex items-center justify-between border rounded-xl px-4 py-3 transition-all cursor-pointer`}
                  onClick={() => setSelectedValue('loaded')}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={selectedValue === 'loaded'}
                      readOnly
                      className="accent-purple-500 w-4 h-4 dark:bg-white"
                    />
                    <span className="font-medium">
                      {t('Loaded')} ({loadedComments})
                    </span>
                  </div>
                  <span className="text-purple-500 font-medium text-sm whitespace-nowrap">0 {t('Credits')}</span>
                </li>
              </ul>
              <div className="flex gap-3 w-full">
                <button
                  className="flex-1 h-10 rounded-full border border-gray-300 font-medium text-gray-700 bg-white hover:bg-gray-100 transition"
                  onClick={onCancel}
                >
                  {t('Cancel')}
                </button>
                <button
                  className="flex-1 h-10 rounded-full font-medium text-white bg-gradient-to-b from-[#7763FF] to-[#A29AFF] shadow"
                  onClick={handleExportNow}
                >
                  {t('Export Now')}
                </button>
              </div>
            </div>
          ) : (
            <div className="relative bg-white rounded-2xl shadow-xl w-[450px] max-w-[90vw] p-8 flex flex-col animate-fade-in">
              <div className="flex items-center justify-between mb-2">
                <span className="text-base font-medium">{t('Export in progress')}...</span>
                <span className="text-gray-500 text-base">{progressText}</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-2 bg-green-500 rounded-full" style={{ width: `${progressPercent}%` }}></div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
